import React, { Component, useCallback, useEffect, useState } from 'react';
import { UserForgotPassword } from 'components/UserForgotPassword/UserForgotPassword';
import { UserLogin } from 'components/UserLogin/UserLogin';
import { UserSignup } from 'components/UserSignup/UserSignup';
import { Modal } from 'components/ui/Modal/Modal';
import { Logo } from 'containers/Logo/Logo';
import { LoginContextProvider } from '../../contexts/LoginContext/LoginContext';
import { useTranslations } from 'utils';
import { Tabs } from 'components/Tabs/Tabs';
import { TabList } from 'components/Tabs/TabList/TabList';
import { TabPanels } from 'components/Tabs/TabPanels/TabPanels';
import { TabPanel } from 'components/Tabs/TabPanel/TabPanel';
import { Tab } from 'components/Tabs/Tab/Tab';

const DELAY = 125;

export interface UserAccessModalProps {
  isOpen: boolean;
  loginOrigin?: string;
  onClose?: () => void;
  onLoginSuccess?: () => void;
  onSignUpSuccess?: () => void;
  showSignupTab?: boolean;
  allowClosing?: boolean;
  headerLogin?: React.ReactNode;
  headerSignUp?: React.ReactNode;
}

const UserAccessModal = ({
  isOpen,
  loginOrigin,
  onClose = () => null,
  onLoginSuccess = () => null,
  onSignUpSuccess = () => null,
  showSignupTab = false,
  allowClosing = true,
  headerLogin,
  headerSignUp,
}: UserAccessModalProps) => {
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showUpdatePasswordAlert, setShowUpdatePasswordAlert] = useState(false);

  const handleClose = () => {
    setShowResetPassword(false);
    onClose();
  };

  const onResetPassword = (showAlert = false) => {
    setShowResetPassword(prev => !prev);
    setShowUpdatePasswordAlert(showAlert);
  };

  return (
    <Modal isOpen={isOpen} allowClosing={allowClosing} onClose={handleClose}>
      <div
        className="textAlign-center marginBottom-md"
        data-testid="userAccessModal"
      >
        <Logo />
      </div>
      {showResetPassword ? (
        <UserForgotPassword
          onLogin={onResetPassword}
          showUpdatePasswordAlert={showUpdatePasswordAlert}
        />
      ) : (
        <UserAccessTabs
          loginOrigin={loginOrigin}
          onLoginSuccess={onLoginSuccess}
          onSignUpSuccess={onSignUpSuccess}
          showSignupTab={showSignupTab}
          onResetPassword={onResetPassword}
          headerLogin={headerLogin}
          headerSignUp={headerSignUp}
        />
      )}
    </Modal>
  );
};

interface UserAccessTabsProps {
  onLoginSuccess: () => void;
  onResetPassword: () => void;
  onSignUpSuccess: () => void;
  showSignupTab: boolean;
  loginOrigin?: string;
  headerLogin?: React.ReactNode;
  headerSignUp?: React.ReactNode;
}

const UserAccessTabs = ({
  onLoginSuccess,
  onSignUpSuccess,
  onResetPassword,
  showSignupTab,
  loginOrigin,
  headerLogin,
  headerSignUp,
}: UserAccessTabsProps) => {
  const i18n = useTranslations();
  const loginTabIndex = 0;
  const signupTabIndex = 1;
  const defaultTab = showSignupTab ? signupTabIndex : loginTabIndex;
  const loginFirstInputRef = React.useRef<HTMLInputElement | null>(null);
  const signupFirstInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleOnTabChange = useCallback(({ index }: { index: number }) => {
    const textInput =
      index === loginTabIndex
        ? loginFirstInputRef.current
        : signupFirstInputRef.current;

    if (textInput) {
      setTimeout(() => textInput.focus(), DELAY);
    }
  }, []);

  useEffect(() => {
    handleOnTabChange({ index: loginTabIndex });
  }, [handleOnTabChange]);

  return (
    <LoginContextProvider loginOrigin={loginOrigin}>
      <Tabs defaultActiveIndex={defaultTab} onChange={handleOnTabChange}>
        <TabList>
          <Tab data-testid="userAccessModal-login">
            {i18n.t('userAccessModal.login')}
          </Tab>
          <Tab data-testid="userAccessModal-signup">
            {i18n.t('userAccessModal.signup')}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {({ selectTab, isActive }) =>
              isActive && (
                <UserLogin
                  onLoginSuccess={onLoginSuccess}
                  handleSignUpClick={() => selectTab(signupTabIndex)}
                  resetPassword={onResetPassword}
                  firstInputRef={loginFirstInputRef}
                  header={headerLogin}
                />
              )
            }
          </TabPanel>
          <TabPanel>
            {({ selectTab, isActive }) =>
              isActive && (
                <UserSignup
                  onSignUpSuccess={onSignUpSuccess}
                  handleLoginClick={() => selectTab(loginTabIndex)}
                  firstInputRef={signupFirstInputRef}
                  header={headerSignUp}
                />
              )
            }
          </TabPanel>
        </TabPanels>
      </Tabs>
    </LoginContextProvider>
  );
};

export { UserAccessModal };
